import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { AppContext } from '../../../contexts/AppContext';
import axiosInstance from '../../../config/axios';
import { assets } from '../../../assets/assets';
import SelectProjectEmployeesModal from './SelectProjectEmployeesModal';

const EMPTY_FORM = { titre: '', description: '', priorite: 'normale', date_echeance: '' };

const toDateInput = (value) => (value ? new Date(value).toISOString().split('T')[0] : '');
const toFrDate = (value) => new Date(value).toLocaleDateString('fr-FR');

/**
 * Secura Web : création d'une tâche de projet en fenêtre modale (même rendu que « Nouveau shift »).
 * Avec `taskId`, la même fenêtre sert à modifier la tâche.
 */
const CreateTaskModal = ({ isOpen, onClose, projetId, onCreated, taskId = null, onUpdated }) => {
  const isEdit = Boolean(taskId);
  const { userData } = useContext(AppContext);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [projet, setProjet] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setSelectedEmployees([]);
      setShowEmployeesModal(false);
      return undefined;
    }

    // Seuls les membres actifs du projet peuvent recevoir la tâche
    let cancelled = false;
    (async () => {
      setEmployeesLoading(true);
      try {
        if (isEdit) {
          const taskResponse = await axiosInstance.get(`/tasks/${taskId}`);
          const task = taskResponse.data?.data || taskResponse.data?.task;
          if (cancelled || !task) return;
          setFormData({
            titre: task.titre || '',
            description: task.description || '',
            priorite: task.priorite || 'normale',
            date_echeance: toDateInput(task.date_echeance)
          });
          setSelectedEmployees(
            (task.assignments || []).map(a => Number(a.employee_id || a.employee?.id)).filter(Boolean)
          );
        }

        const response = await axiosInstance.get(`/projets/${projetId}`);
        let projetData = response.data?.data || response.data?.projet;
        if (typeof projetData === 'string') projetData = JSON.parse(projetData);
        if (cancelled || !projetData) return;
        setProjet(projetData);

        const seen = new Set();
        setEmployees(
          (projetData.assignments || [])
            .map(a => a?.employee)
            .filter(emp => emp?.id && emp.statut === 'actif' && !seen.has(emp.id) && seen.add(emp.id))
        );
      } catch (error) {
        console.error('Erreur chargement projet:', error);
        if (!cancelled) toast.error('Erreur lors du chargement du projet');
      } finally {
        if (!cancelled) setEmployeesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, projetId, taskId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const selectedEmployeesData = employees.filter(emp =>
    selectedEmployees.some(id => String(id) === String(emp.id))
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missingFields = [];
    if (!formData.titre.trim()) missingFields.push('Titre');
    if (!formData.description.trim()) missingFields.push('Description');
    if (!formData.date_echeance) missingFields.push('Date d\'échéance');
    if (missingFields.length > 0) {
      toast.error(`Veuillez renseigner tous les champs obligatoires: ${missingFields.join(', ')}`);
      return;
    }

    // La date d'échéance doit rester dans l'intervalle du projet
    if (projet?.date_debut && formData.date_echeance < toDateInput(projet.date_debut)) {
      toast.error(`La date d'échéance doit être supérieure ou égale à la date de début du projet (${toFrDate(projet.date_debut)})`);
      return;
    }
    if (projet?.date_fin_prevue && formData.date_echeance > toDateInput(projet.date_fin_prevue)) {
      toast.error(`La date d'échéance doit être inférieure ou égale à la date de fin prévue du projet (${toFrDate(projet.date_fin_prevue)})`);
      return;
    }

    try {
      setIsLoading(true);
      if (isEdit) {
        const response = await axiosInstance.put(`/tasks/${taskId}`, {
          titre: formData.titre.trim(),
          description: formData.description,
          priorite: formData.priorite,
          date_echeance: formData.date_echeance,
          employee_ids: selectedEmployees
        });
        if (!response.data.success) {
          toast.error(response.data.message || 'Erreur lors de la modification');
          return;
        }
        toast.success('Tâche modifiée avec succès');
        onClose();
        onUpdated?.();
        return;
      }

      const response = await axiosInstance.post('/tasks', {
        titre: formData.titre.trim(),
        description: formData.description,
        priorite: formData.priorite,
        date_echeance: formData.date_echeance,
        department_id: projet?.department_id || userData?.department_id,
        employee_ids: selectedEmployees,
        projet_id: projetId
      });

      if (!response.data.success) {
        toast.error(response.data.message || 'Erreur lors de la création');
        return;
      }

      toast.success('Tâche créée avec succès');
      onClose();
      onCreated?.();
    } catch (error) {
      console.error(isEdit ? 'Erreur modification tâche:' : 'Erreur création tâche:', error);
      toast.error(error.response?.data?.message || (isEdit ? 'Erreur lors de la modification de la tâche' : 'Erreur lors de la création de la tâche'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div className="bg-white rounded-2xl shadow-2xl p-4 w-full max-w-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-base font-bold text-gray-900 truncate">{isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {projet?.nom ? (
              <div className="w-full px-4 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 bg-gray-50 text-center truncate">
                {projet.nom}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                className={inputClass}
                placeholder="Titre de la tâche"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className={`${inputClass} resize-none`}
                placeholder="Description de la tâche..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priorité <span className="text-red-500">*</span>
                </label>
                <select name="priorite" value={formData.priorite} onChange={handleChange} className={inputClass} required>
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Échéance <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date_echeance"
                  value={formData.date_echeance}
                  onChange={handleChange}
                  min={toDateInput(projet?.date_debut) || undefined}
                  max={toDateInput(projet?.date_fin_prevue) || undefined}
                  className={inputClass}
                  required
                />
              </div>
            </div>
            {projet && (projet.date_debut || projet.date_fin_prevue) ? (
              <p className="text-xs text-gray-500 -mt-2">
                Entre {projet.date_debut ? toFrDate(projet.date_debut) : '...'} et {projet.date_fin_prevue ? toFrDate(projet.date_fin_prevue) : '...'}
              </p>
            ) : null}

            {/* Bouton de sélection des employés (membres du projet) */}
            <button
              type="button"
              onClick={() => setShowEmployeesModal(true)}
              className="w-full px-4 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3"
            >
              {selectedEmployees.length > 0 ? (
                <>
                  <span>Employés</span>
                  <div className="flex items-center -space-x-2">
                    {selectedEmployeesData.slice(0, 3).map((emp, idx) => {
                      const profileImageUrl = emp.profileImageUrl || emp.imageProfil || emp.image_profil || emp.photo || emp.avatar;
                      return (
                        <div
                          key={emp.id}
                          className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
                          style={{ zIndex: 10 - idx }}
                        >
                          <img
                            src={profileImageUrl || assets.employes}
                            alt={emp.nom_complet || 'Employé'}
                            className={`w-full h-full border-2 border-white rounded-full ${profileImageUrl ? 'object-cover' : 'object-contain'}`}
                            onError={(e) => { e.target.src = assets.employes; }}
                          />
                        </div>
                      );
                    })}
                    {selectedEmployees.length > 3 && (
                      <div
                        className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-semibold text-gray-700 bg-gray-100 flex-shrink-0"
                        style={{ borderColor: '#0026E2', zIndex: 0 }}
                      >
                        +{selectedEmployees.length - 3}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                'Sélectionner les employés (optionnel)'
              )}
            </button>

            <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 border border-gray-300 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2 bg-[#0026E2] text-white rounded-2xl font-semibold hover:bg-[#0018B8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isEdit ? 'Enregistrement...' : 'Création...'}</span>
                  </>
                ) : (
                  isEdit ? 'Enregistrer' : 'Créer la tâche'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <SelectProjectEmployeesModal
        isOpen={showEmployeesModal}
        onClose={() => setShowEmployeesModal(false)}
        employees={employees}
        loading={employeesLoading}
        selectedEmployees={selectedEmployees}
        onSelectEmployees={setSelectedEmployees}
      />
    </>,
    document.body
  );
};

export default CreateTaskModal;
